import { getQueryCacheSnapshot, invalidateQuery } from './queryCache';

export const QUERY_CACHE_DEBUG_FLAG = 'cfr:debug-query-cache';

export interface IQueryCacheDebugEntry {
  key: string,
  value: unknown,
  expiresIn: string,
  subscribers: number,
}

export interface IQueryCacheDebugHandle {
  entries: () => IQueryCacheDebugEntry[],
  invalidate: (keyOrPrefix: string) => void,
}

declare global {
  interface Window {
    __CFR_QUERY_CACHE__?: IQueryCacheDebugHandle,
  }
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (ms < 1000) {
    return `${ ms }ms`;
  }

  if (seconds < 60) {
    return `${ seconds }s`;
  }

  if (minutes < 60) {
    return `${ minutes }m ${ seconds % 60 }s`;
  }

  return `${ hours }h ${ minutes % 60 }m`;
}

function formatExpiresIn(expiresAt: number): string {
  const remainingMs = expiresAt - Date.now();

  return remainingMs < 0 ? `expired ${ formatDuration(-remainingMs) } ago` : formatDuration(remainingMs);
}

const debugHandle: IQueryCacheDebugHandle = {
  entries: () => getQueryCacheSnapshot().map(({ key, value, expiresAt, subscribers }) => ({
    key,
    value,
    expiresIn: formatExpiresIn(expiresAt),
    subscribers,
  })),
  invalidate: (keyOrPrefix) => invalidateQuery(keyOrPrefix),
};

function isDebugEnabled(): boolean {
  try {
    return window.localStorage.getItem(QUERY_CACHE_DEBUG_FLAG) === '1';
  } catch {
    return false;
  }
}

// Opt-in: localStorage.setItem('cfr:debug-query-cache', '1'), then reload.
export function exposeQueryCacheDebug(): void {
  if (typeof window !== 'undefined' && isDebugEnabled()) {
    window.__CFR_QUERY_CACHE__ = debugHandle;
  }
}
